import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import {
  exchangeCodeForTokens,
  popOAuthState,
  redirectToAuthorize,
} from '../../auth/authService';

/**
 * SSOCallback
 *
 * Mounted at /auth/callback.
 *
 * After the auth server's /authorize endpoint generates a code, it redirects
 * here with:
 *
 *   /auth/callback?code=<authorization_code>&state=<csrf_state>
 *
 * Or on error:
 *
 *   /auth/callback?error=<error_code>&state=<csrf_state>
 *
 * This component:
 *   1. Checks for authorization errors from the server.
 *   2. Verifies the `state` param matches the saved CSRF value.
 *   3. Exchanges the `code` for tokens via POST /api/v1/auth/token.
 *   4. Stores tokens (including id_token) via AuthContext.signIn().
 *   5. Navigates the user to their original destination (or /dashboard).
 */
const SSOCallback: React.FC = () => {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const handled = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(window.location.search);
    const code       = params.get('code');
    const stateIn    = params.get('state');
    const stateSaved = popOAuthState();

    // Error responses are OAuth responses too: validate their state before
    // trusting or acting on them, and never auto-restart an unverified flow.
    const authError = params.get('error');
    if (authError) {
      if (!stateSaved || stateIn !== stateSaved) {
        console.error('[SSOCallback] error response state mismatch (CSRF?)', { stateIn, stateSaved });
        setError(t('auth:ssoCallback.errorAuthFailed'));
        return;
      }
      const desc = params.get('error_description') || authError;
      // A silent SSO probe (prompt=none) answering "not signed in" / "no
      // consent yet" is an expected outcome, not a failure to show the user.
      // Fall back to the normal pre-sign-in screen quietly. The probe is
      // one-shot per tab (Onboarding sets a sessionStorage latch), so this
      // cannot loop.
      if (authError === 'login_required' || authError === 'consent_required') {
        console.info('[SSOCallback] silent SSO probe:', authError);
        navigate('/onboarding', { replace: true });
        return;
      }
      console.error('[SSOCallback] authorization error:', authError, desc);
      setError(t('auth:ssoCallback.errorAuthorizationFailed', { desc }));
      return;
    }

    // Security: verify CSRF state.
    //
    // Three failure shapes here:
    //   (a) No `code` in URL  → auth server didn't send us anything,
    //       fall through to redirectToAuthorize (real error path).
    //   (b) No `stateSaved`   → first-time landing on platform-web
    //       (typical post-invite flow: id.svdy.com/accept-invite →
    //       sign-in → /auth/callback). sessionStorage is empty
    //       because the user never invoked redirectToAuthorize() on
    //       this origin. Silently retry: redirectToAuthorize will
    //       save a fresh state, the auth server's session cookie
    //       takes the user through SSO, and the second callback
    //       arrives with matching state. Render falls through to the
    //       'Signing you in…' spinner instead of a scary error popup.
    //   (c) stateIn !== stateSaved → real CSRF mismatch. Loud error.
    if (!code) {
      console.error('[SSOCallback] missing code', { code, stateIn });
      setError(t('auth:ssoCallback.errorAuthFailed'));
      setTimeout(() => redirectToAuthorize(), 2500);
      return;
    }
    if (!stateSaved) {
      // Benign first-time / fresh-tab landing — silent retry. The
      // existing render path shows 'Signing you in…' while we wait
      // for the redirect to fire.
      console.info('[SSOCallback] no saved oauth state; silent retry (likely first-time invite acceptance)');
      setTimeout(() => redirectToAuthorize(), 100);
      return;
    }
    if (stateIn !== stateSaved) {
      console.error('[SSOCallback] state mismatch (CSRF?)', { stateIn, stateSaved });
      setError(t('auth:ssoCallback.errorAuthFailed'));
      setTimeout(() => redirectToAuthorize(), 2500);
      return;
    }

    // Remove code/state from the URL immediately (security hygiene)
    window.history.replaceState({}, '', window.location.pathname);

    exchangeCodeForTokens(code, stateIn).then((tokens) => {
      if (!tokens) {
        setError(t('auth:ssoCallback.errorCodeExchange'));
        setTimeout(() => redirectToAuthorize(), 2500);
        return;
      }
      signIn(tokens.access_token, tokens.refresh_token, tokens.expires_in, tokens.id_token);

      // Hand off to the OrgGate, which resolves / creates the active
      // organization context. The user's post-auth return URL (if any)
      // is consumed there, not here — OrgGate needs it to know where
      // to send a single-org user after auto-selecting.
      navigate('/auth/select-org', { replace: true });
    });
  }, [signIn, navigate, t]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      {error ? (
        <Alert severity="error" sx={{ maxWidth: 420 }}>{error}</Alert>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Signing you in…
          </Typography>
        </>
      )}
    </Box>
  );
};

export default SSOCallback;

